import {getId, getType} from 'apis/model/base'
import {timetable_create_Api, timetable_delete_Api, timetable_update_Api} from 'apis'
import {Modal} from 'antd'
import React, {useCallback, useMemo, useState} from 'react'
import {NullAsDefault, renderIf} from 'views/Shared'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {TimetableActionTypes} from 'views/AppLocals/Course'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {MdOutlineControlPointDuplicate} from 'react-icons/md'
import {TimetableCopyModal, TimetableCreationModal, TimetableEditingModal} from './TimetableForm'

// Timetable thuộc về 1 course_class. Tách riêng khỏi useTimetableOrganizationForm.js để mỗi
// hook chỉ lo đúng 1 loại owner, tránh nhánh if/else owner_type rải rác dễ gây nhầm lẫn.
export const useTimetableCourseClassForm = ({
                                                course_class,
                                                refreshKey: externalRefreshKey,
                                                setRefreshKey: externalSetRefreshKey,
                                                ...props
                                            } = {}) => {
    const t = useTranslate()

    const [internalRefreshKey, setInternalRefreshKey] = useState()
    const refreshKey = externalRefreshKey !== undefined ? externalRefreshKey : internalRefreshKey
    const setRefreshKey = externalSetRefreshKey || setInternalRefreshKey

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onError = notifyOnError(t)

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        setRefreshKey(Date.now())
        onCancel()
    }

    const {handleAsyncAction: onAssign} = useAsyncAction({
        apiInfo: timetable_create_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.INFORMATION, t)
            onRefresh()
        },
    })

    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: timetable_update_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onCancel()
        },
    })

    const {handleAsyncAction: onRevoke} = useAsyncAction({
        apiInfo: timetable_delete_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.INFORMATION, t)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case TimetableActionTypes.TIMETABLE_CREATE:
            case TimetableActionTypes.TIMETABLE_UPDATE:
            case TimetableActionTypes.TIMETABLE_DUPLICATE:
                setAction({type: action, value: params})
                break
            case TimetableActionTypes.TIMETABLE_DELETE:
                const id = params?.id
                if (id) {
                    showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete timetable item',
                        message: 'are you sure you want to delete this timetable item',
                        onOk: () => {
                            onRevoke({}, {id: id})
                        },
                    })
                }
                break
            default:
                break
        }
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <MdOutlineControlPointDuplicate size={14} className="text-green-500"/>,
                        label: 'duplicate',
                        key: TimetableActionTypes.TIMETABLE_DUPLICATE,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: TimetableActionTypes.TIMETABLE_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: TimetableActionTypes.TIMETABLE_DELETE,
                    },
                ].filter((e) => item),
                (key) => {
                    handleAction(key, item)
                }
            ),
        []
    )

    const modal = useMemo(() => {
        let title
        let Content = NullAsDefault
        let params = {title: undefined}
        let modal_params = {}

        const {Header = NullAsDefault} = props ?? {}

        const _course_class = course_class ?? action?.value?.course_class ?? {}
        const {owner: courseOwner, course} = _course_class ?? {}
        const course_id = getId(course)
        const course_class_id = getId(_course_class)
        const courseOwnerId = getId(courseOwner)
        const courseOwnerType = getType(courseOwner)

        switch (action?.type) {
            case TimetableActionTypes.TIMETABLE_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('add timetable for the class')}
                    </div>
                )
                params = {
                    onSubmit: (values) => {
                        onAssign({...values, owner_type: 'course_class', owner_id: course_class_id}, {})
                    },
                }
                modal_params = {width: 640}
                Content = TimetableCreationModal
                break
            case TimetableActionTypes.TIMETABLE_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit timetable for the class')}
                    </div>
                )
                params = {
                    item: action?.value,
                    onSubmit: (values) => {
                        onUpdate(values, {id: action?.value?.id})
                    },
                }
                modal_params = {width: 700}
                Content = TimetableEditingModal
                break
            case TimetableActionTypes.TIMETABLE_DUPLICATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('duplicate timetable')}
                    </div>
                )
                params = {
                    item: action?.value,
                    onSubmit: (values) => {
                        onAssign({...values, owner_type: 'course_class', owner_id: course_class_id}, {})
                    },
                }
                modal_params = {width: 700}
                Content = TimetableCopyModal
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <div className="flex flex-col gap-3">
                    {renderIf(
                        action?.type === TimetableActionTypes.TIMETABLE_DUPLICATE,
                        <div className="space-x-1 text-sm italic">
                            <span className="font-bold text-color-100">
                                {`* ${t('note')} : `}
                            </span>
                            <span className="text-color-400">
                                {t('duplicate timetable description')}
                            </span>
                        </div>
                    )}
                    <Header item={_course_class}/>
                    <Content
                        {...params}
                        owner_type="course_class"
                        course_id={course_id}
                        course_class_id={course_class_id}
                        courseOwnerType={courseOwnerType}
                        courseOwnerId={courseOwnerId}
                    />
                </div>
            </Modal>
        )
    }, [action, course_class, props.Header])

    return {
        modal,
        action,
        onRefresh,
        onCancel,
        refreshKey,
        handleAction,
        renderContextMenu,
    }
}
